// The card menu's two move items (#6772, epic #6552; prototype cmenuHTML `:1863-1864`): "Move
// work", which opens the Move work panel, and "Drain machine", which opens it with everything
// movable ticked. Their own group, right after Send work's, for an admin on an own-org machine
// on the CARD only (`cardMenu`, `./machine-card.ts`); `MissionCardMenu` drops both when the
// card has no move handle (`useCardMove`), as it drops Send work without its controller.
//
// BOTH ARE `aria-disabled` (still in the arrow-key order, saying why in their `title`) when the
// machine is offline ("Offline"), when nothing runs here ("None") or when the org has fewer
// than two machines. Drain is also disabled while the page's own pre-send check holds
// (`MovePage.blockedWhy`: the page guard locked, or this machine's moves read 0); Move work
// still opens, and its panel's why line says it. Drain sends no drain request and does not
// cordon the machine: its quiet second line says so.

import type { CardMenuItem } from './card-menu-types';
import { OFFLINE_STATUSES } from './fleet-machines';
import type { UtilBox } from './fleetUtilization';
import { boxJobs, jobState } from './machine-jobs';

/** What the two items read beyond the machine. */
export type MoveMenuOptions = {
  /** This org's own machines on the page: Move work needs another. */
  readonly machines: number;
  /** The page's pre-send check on this machine: the guard locked, the moves read 0, or none. */
  readonly blocked: 'locked' | 'limit' | null;
};

const BLOCKED_TITLE = {
  locked: 'fleet.move.grip_why_locked',
  limit: 'fleet.move.grip_why_limit',
} as const;

/** Why neither item can open on this machine, as a disabled item's detail and title. */
function cannotOpen(
  box: UtilBox,
  machines: number,
): Pick<CardMenuItem, 'detailKey' | 'title'> | 'solo' | null {
  if (OFFLINE_STATUSES.includes(box.verdict.status)) {
    return { detailKey: 'fleet.move.menu_offline', title: 'fleet.move.menu_offline_title' };
  }
  if (!boxJobs(box).some((j) => jobState(j) !== 'last_known')) {
    return { detailKey: 'fleet.move.menu_none', title: 'fleet.move.menu_nothing_title' };
  }
  return machines < 2 ? 'solo' : null;
}

/** "Move work" and "Drain machine", in that order. */
export function moveWorkItems(box: UtilBox, o: MoveMenuOptions): readonly CardMenuItem[] {
  const no = cannotOpen(box, o.machines);
  const move: CardMenuItem = {
    id: 'move_work',
    label: 'fleet.move.menu_move',
    title: 'fleet.move.menu_move_title',
    ...(no === null ? {} : { disabled: true as const }),
    ...(no === null || no === 'solo' ? {} : no),
  };
  const blocked = no === null && o.blocked !== null;
  const drain: CardMenuItem = {
    id: 'drain_select',
    label: 'fleet.move.menu_drain',
    title: 'fleet.move.menu_drain_title',
    noteKey: 'fleet.move.work_drain_note',
    ...(no === null && !blocked ? {} : { disabled: true as const }),
    ...(no === null || no === 'solo' ? {} : no),
    ...(blocked && o.blocked !== null ? { title: BLOCKED_TITLE[o.blocked] } : {}),
  };
  return [move, drain];
}
